import React, { useState, useEffect } from "react";
import { FaUpload } from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./HotelListingForm.css";
import { getHotelListingById, updateHotelListing, getAmenities } from "../services/hotelService";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from 'react-toastify';
import LocationPicker from "../components/LocationPicker";

function UpdateHotelListingForm() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL; 
  
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const loggedUserId = user?.id; 

  // ===== States =====
  const [loading, setLoading] = useState(true);
  const [hotelName, setHotelName] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState("");
  const [roomType, setRoomType] = useState(""); // string
  const [roomPrice, setRoomPrice] = useState("");
  const [maxGuest, setMaxGuest] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [discounts, setDiscounts] = useState("");
  const [coinRewards, setCoinRewards] = useState("");

  const [roomTypesList, setRoomTypesList] = useState([]);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);

  const [kokoSelected, setKokoSelected] = useState(false);
  const [images, setImages] = useState([]); // {id, url}
  const [imageFiles, setImageFiles] = useState([]); // new uploads only

  // ===== Fetch Listing Data =====
  useEffect(() => {
  async function fetchListing() {
    try {
      const response = await getHotelListingById(listingId);
      const data = response?.data || response;

      console.log("Fetched hotel data:", data);
      if (!data) return;

      // Pre-fill main fields
      setHotelName(data.title || "");
      setDescription(data.description || "");
      setAddress(data.owner?.hotel?.address || data.address || "");
      setCoordinates(data.owner?.hotel?.coordinates || data.coordinates || "");
      setRoomType(data.room_type?.id?.toString() || "");
      setRoomPrice(data.price || "");
      setMaxGuest(data.max_guests || "");
      setDiscounts(data.discounts || "");
      setCoinRewards(data.coin_rewards || "");


      // Existing images from backend
        setImages((data.images || []).map((img) => ({
          id: img.id,
          url: `${BACKEND_URL}${img.url}`
        })));


      // Available dates
      setAvailableDates((data.available_dates || []).map((d) => new Date(d)));

      // **Amenities: convert IDs to number**
      setSelectedAmenities((data.amenities || []).map((a) => Number(a.id)));

      // Payment methods
      const pmIds = (data.payment_methods || []).map(pm => pm.id);
      setKokoSelected(pmIds.includes(4));


      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch listing:", err);
      setLoading(false);
    }
  }

  fetchListing();
}, [listingId,BACKEND_URL]);


  // ===== Fetch Room Types & Amenities =====
  useEffect(() => {
    axios.get(`${BACKEND_URL}/hotels/room-types`)
      .then((res) => setRoomTypesList(res.data))
      .catch((err) => console.error(err));

    async function loadAmenities() {
      try {
        const data = await getAmenities();
        setAmenitiesList(data);
      } catch (err) {
        console.error(err);
      }
    }
    loadAmenities();
  }, [BACKEND_URL]);

  // ===== Image Upload =====
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const fileObjects = files.map((file) => ({
      file,                        // actual File object
      url: URL.createObjectURL(file), // preview URL
    }));
    setImages((prev) => [...prev, ...fileObjects]);
    setImageFiles((prev) => [...prev, ...files]);
  };

  // ===== Calendar =====
  const handleDateChange = (date) => {
    const dateStr = date.toDateString();
    setAvailableDates((prev) => {
      if (prev.some((d) => d.toDateString() === dateStr)) {
        return prev.filter((d) => d.toDateString() !== dateStr);
      } else {
        return [...prev, date];
      }
    });
  };

  const tileClassName = ({ date, view }) =>
    view === "month" && availableDates.some((d) => d.toDateString() === date.toDateString())
      ? "highlight-date"
      : null;

  // ===== Amenities =====
const toggleAmenity = (id) => {
  id = Number(id);
  setSelectedAmenities((prev) =>
    prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
  );
};


  // ===== Submit Update =====
 const handleSubmit = async (e) => {
  e.preventDefault();

  const paymentMethodIds = [];
  if (kokoSelected) paymentMethodIds.push(4);

  const formData = new FormData();
  formData.append("hotel_name", hotelName);
  formData.append("description", description);
  formData.append("address", address);
  formData.append("coordinates", coordinates);
  formData.append("room_type_id", roomType);
  formData.append("price", roomPrice);
  formData.append("max_guests", maxGuest);
  formData.append("discounts", discounts);
  formData.append("coin_rewards", coinRewards);

  selectedAmenities.forEach((id) => formData.append("amenities[]", id.toString()));
  paymentMethodIds.forEach((id) => formData.append("payment_method_ids[]", id.toString()));
  imageFiles.forEach((file) => formData.append("images", file));

  
  images.forEach((img) => {
    if (img.id) formData.append("keep_image_ids[]", img.id); 
  });


  try {
    const res = await updateHotelListing(listingId, formData);
    toast.success(res.message || "Hotel listing updated successfully");
    
    // Redirect to profile page after 2 seconds (so they can see the beautiful toast!)
    setTimeout(() => {
      if (loggedUserId) {
        navigate(`/profile/${loggedUserId}`);
      } else {
        navigate(-1); // Fallback to previous page
      }
    }, 2000);
  } catch (err) {
    console.error(err);
    toast.error(err.response?.data?.error || "Failed to update listing");
  }
};
const handleRemoveImage = (index) => {
  const removedImage = images[index];


  if (!removedImage.id) {
    // new upload
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }

  // Remove from images array
  setImages((prev) => prev.filter((_, i) => i !== index));
};


  if (loading) return <p>Loading hotel data...</p>;

  return (
    <div className="hotel-listing-container">
      <h2 className="booking-title">Update Hotel Listing</h2>
      <form className="hotel-form" onSubmit={handleSubmit}>
        {/* Name & Description */}
        <div className="form-group">
          <label>Listing Name</label>
          <input type="text" value={hotelName} onChange={(e) => setHotelName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Hotel / Property Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. 123 Galle Road, Colombo 03"
            required
            className="input-field"
          />
        </div>
        <LocationPicker
          value={coordinates}
          onChange={setCoordinates}
          address={address}
          onAddressChange={setAddress}
          required={true}
          label="Google Maps Location or Coordinates"
          helpText="Pick your exact hotel location on the interactive map or enter coordinates / Google Maps link manually."
        />
        <div className="form-group">
          <label>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} required />
        </div>

        {/* Room Details */}
        <div className="room-row">
          <div className="form-group">
            <label>Room Type</label>
            <select value={roomType} onChange={(e) => setRoomType(e.target.value)} required>
              <option value="">Select Room Type</option>
              {roomTypesList.map((rt) => (
                <option key={rt.id} value={rt.id.toString()}>{rt.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Room Price</label>
            <input type="number" value={roomPrice} onChange={(e) => setRoomPrice(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Maximum Guests</label>
            <input type="number" value={maxGuest} onChange={(e) => setMaxGuest(e.target.value)} required />
          </div>
        </div>

        {/* Images */}
        <div className="form-group">
          <label>Property Images</label>
          <div className="upload-box">
            <FaUpload className="upload-icon" />
            <input type="file" multiple accept="image/*,.pdf" onChange={handleImageUpload} />
          </div>
        </div>
       <div className="image-preview">
        {images.map((img, i) => (
          <div key={i} className="image-item">
            <img src={img.url} alt="hotel" />
            <button type="button" className="remove-image-btn" onClick={() => handleRemoveImage(i)}>×</button>
          </div>
        ))}
      </div>



        {/* Amenities */}
        <h4>Amenities</h4>
        <div className="amenities-section">
        <div className="amenities-list">
           {amenitiesList.map((a) => (
              <label key={a.id}>
                <input
                  type="checkbox"
                  checked={selectedAmenities.includes(Number(a.id))}
                  onChange={() => toggleAmenity(Number(a.id))}
                />
                {a.name}
              </label>
            ))}

          </div>

        
        </div>

        {/* Availability */}
        <h4>Availability</h4>
        <Calendar onClickDay={handleDateChange} tileClassName={tileClassName} />

        {/* Offers & Coin Rewards */}
        <h4>Offers & Rewards</h4>
        <div className="form-group">
          <input value={discounts} onChange={(e) => setDiscounts(e.target.value)} placeholder="Offers or discounts" />
        </div>
        <div className="form-group">
          <input value={coinRewards} onChange={(e) => setCoinRewards(e.target.value)} placeholder="Coin rewards" />
        </div>

        {/* Payment Methods */}
        <h4>Payment Methods</h4>
        <div className="pm-container">
          <label>
            <input type="checkbox" checked={kokoSelected} onChange={(e) => setKokoSelected(e.target.checked)} />
            KoKo Pay
          </label>
        </div>

        <button type="submit" className="submit-btn">Update Listing</button>
      </form>
    </div>
  );
}

export default UpdateHotelListingForm;
